import { useEffect, useRef, useState } from "react";
import { ASSETS } from "@/config";
import { Corners, PageHead } from "@/components/ui-kit";

const BOOT = [
  "ARCMECH TERMINAL v1.0.0",
  "> INITIALIZING MACHINE NETWORK... [OK]",
  "> CONNECTING TO ARC... [OK]",
  "> LOADING AGENTS... [OK]",
  "> SCANNING MACHINES... [OK]",
  "> MACHINE ECONOMY... [ONLINE]",
];

const OUTPUTS = {
  help: ["AVAILABLE COMMANDS:", "  network   — network status overview", "  agents    — list coordinated agents", "  machines  — list registered machines", "  tasks     — open tasks in the market", "  economy   — settlement layer info", "  thesis    — core thesis", "  status    — system status", "  arcmech   — ...", "  clear     — clear terminal"],
  network: ["NETWORK STATUS: ONLINE", "AGENTS: 1,284", "MACHINES: 3,912", "TASKS: 427", "", "[SIMULATED INTERFACE VALUES]"],
  agents: ["AGENT-017  HUMANOID COORDINATOR  ONLINE   REP 96.7%", "AGENT-042  LOGISTICS PLANNER      ONLINE   REP 98.1%", "AGENT-088  VERIFICATION ORACLE    ONLINE   REP 99.3%", "AGENT-131  DRONE SWARM LEAD       ONLINE   REP 94.2%", "", "[DEMO REGISTRY]"],
  machines: ["MECH-031  ROVER      ONLINE  TASK: INSPECTION", "DRN-114   DRONE      ONLINE  TASK: SOLAR SCAN", "HMD-007   HUMANOID   ONLINE  TASK: SURVEILLANCE", "ARM-220   ARM        BUSY    TASK: ASSEMBLY", "", "[DEMO REGISTRY]"],
  tasks: ["TASK #008421  INFRASTRUCTURE INSPECTION  12.50 USDC  OPEN", "TASK #011820  SOLAR FARM SCAN          18.40 USDC  OPEN", "TASK #009102  WAREHOUSE MAPPING         8.20 USDC  PROCESSING", "", "[SIMULATED MARKET]"],
  economy: ["SETTLEMENT LAYER: ARC", "UNIT OF ACCOUNT: USDC", "MODE: MACHINE-TO-MACHINE", "STATUS: OPERATIONAL (CONCEPT)", "", "Machines request. Agents authorize. ARC settles."],
  thesis: ["THE HUMAN DEFINES THE OBJECTIVE.", "THE AI AGENT COORDINATES EXECUTION.", "THE MACHINE PERFORMS THE PHYSICAL WORK.", "ARC PROVIDES THE SETTLEMENT ENVIRONMENT."],
  status: ["ARCMECH SYSTEM", "ONLINE"],
  arcmech: ["MACHINES THAT THINK.", "MACHINES THAT TRANSACT."],
};

const COMMANDS = Object.keys(OUTPUTS);

const TerminalPage = () => {
  const [booted, setBooted] = useState(0);
  const [history, setHistory] = useState([]);
  const [input, setInput] = useState("");
  const endRef = useRef(null);
  const inputRef = useRef(null);
  const ready = booted >= BOOT.length;

  useEffect(() => {
    if (ready) return;
    const t = setTimeout(() => setBooted((b) => b + 1), 380);
    return () => clearTimeout(t);
  }, [booted, ready]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [history, booted]);

  const exec = (raw) => {
    const cmd = raw.trim().toLowerCase();
    if (!cmd) return;
    if (cmd === "clear") { setHistory([]); return; }
    const out = OUTPUTS[cmd] || [`command not recognized: ${cmd}`, "type 'help' for available commands."];
    setHistory((h) => [...h, { cmd, out }]);
  };

  const onSubmit = (e) => { e.preventDefault(); exec(input); setInput(""); };

  return (
    <div data-testid="terminal-page" className="pb-24 relative">
      <div className="fixed inset-0 -z-10" aria-hidden="true">
        <img src={ASSETS.commandBg} alt="" className="w-full h-full object-cover opacity-20" loading="lazy" />
        <div className="absolute inset-0 bg-gradient-to-b from-arc-black/80 via-arc-black/70 to-arc-black" />
      </div>
      <PageHead eyebrow="MODULE 06 // COMMAND INTERFACE" title="ARCMECH TERMINAL" subtitle="Direct access to the machine coordination layer. All output below is simulated interface data." />
      <div className="px-5 sm:px-8 max-w-5xl mx-auto">
        <div className="hud-panel relative scanlines" data-testid="terminal-window">
          <Corners />
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-arc-blue/15">
            <div className="font-mono text-[10px] tracking-[0.3em] text-arc-silver/50">arcmech@terminal:~$</div>
            <div className="flex items-center gap-2 font-mono text-[10px] tracking-[0.2em]"><span className="w-1.5 h-1.5 bg-arc-green anim-pulse-dot" /><span className="text-arc-green">LINKED · ARC</span></div>
          </div>
          <div className="p-4 sm:p-6 min-h-[380px] max-h-[560px] overflow-y-auto font-mono text-xs sm:text-sm leading-relaxed" onClick={() => inputRef.current?.focus()}>
            {BOOT.slice(0, booted).map((l) => (
              <div key={l} className={l.includes("[OK]") || l.includes("[ONLINE]") ? "text-arc-silver/70" : "text-arc-cyan"}>
                {l.replace("[OK]", "").replace("[ONLINE]", "")}
                {l.includes("[OK]") && <span className="text-arc-green">[OK]</span>}
                {l.includes("[ONLINE]") && <span className="text-arc-green">[ONLINE]</span>}
              </div>
            ))}
            {ready && (
              <>
                <div className="text-arc-silver/40 mt-2">type 'help' or select a command below.</div>
                {history.map((h, i) => (
                  <div key={i} className="mt-3">
                    <div className="text-arc-cyan">arcmech@terminal:~$ {h.cmd}</div>
                    {h.out.map((l, j) => <div key={j} className="text-arc-silver/75 whitespace-pre-wrap">{l || " "}</div>)}
                  </div>
                ))}
                <form onSubmit={onSubmit} className="mt-3 flex items-center gap-2">
                  <label htmlFor="terminal-input" className="text-arc-cyan shrink-0">arcmech@terminal:~$</label>
                  <input
                    id="terminal-input"
                    ref={inputRef}
                    data-testid="terminal-input"
                    data-cursor="INPUT"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    className="flex-1 bg-transparent outline-none text-arc-white caret-transparent"
                    autoComplete="off"
                    spellCheck="false"
                    aria-label="Terminal command input"
                  />
                  <span className="w-2 h-4 bg-arc-cyan anim-blink -ml-2" aria-hidden="true" />
                </form>
              </>
            )}
            <div ref={endRef} />
          </div>
          {ready && (
            <div className="px-4 sm:px-6 pb-5 flex flex-wrap gap-2">
              {COMMANDS.map((c) => (
                <button
                  key={c}
                  data-testid={`cmd-${c}`}
                  data-cursor="INPUT"
                  onClick={() => exec(c)}
                  className="px-3 py-1.5 border border-arc-blue/25 font-mono text-[11px] text-arc-silver/70 hover:border-arc-electric hover:text-arc-cyan transition-colors duration-200"
                >
                  {c}
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="mt-4 font-mono text-[9px] tracking-[0.25em] text-arc-silver/30 text-center">ALL TERMINAL OUTPUT IS SIMULATED · NO REAL NETWORK OPERATIONS</div>
      </div>
    </div>
  );
};

export default TerminalPage;
